import { createRouter, createWebHistory } from 'vue-router';
import {
  checkGeofence,
  ifAuthenticated,
  ifBearerMode,
  ifNotAuthenticated,
  ifPendingMfa,
  ifPendingMfaSetup,
  ifPendingReset,
  ifRfsAuthenticated,
  reloadIntoBearerMode,
  requireMedKit,
  validMedicationId,
} from './guards';

declare module 'vue-router' {
  interface RouteMeta {
    requiresMedKit?: boolean;
  }
}

/*
 * Router factory function for Vite
 */

export default function () {
  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes: [
      {
        path: '/',
        component: () => import('../layouts/NoAuthLayout.vue'),
        beforeEnter: [ifNotAuthenticated, checkGeofence],
        children: [
          { path: '', name: 'login', component: () => import('../pages/Login.vue') },
          {
            path: 'login/agree',
            name: 'agree',
            component: () => import('../pages/login/Agree.vue'),
          },
          {
            path: 'login/updatephone',
            name: 'UpdatePhone.vue',
            component: () => import('../pages/login/UpdatePhone.vue'),
            beforeEnter: ifBearerMode,
          },
          {
            path: 'login/forgot',
            name: 'forgot',
            component: () => import('../pages/login/ForgotPassword.vue'),
          },
          { path: 'login/help', name: 'help', component: () => import('../pages/login/Help.vue') },
          {
            path: 'login/second',
            name: 'second',
            component: () => import('../pages/login/SecondFactor.vue'),
            beforeEnter: ifBearerMode,
          },
          {
            path: 'login/quickstart',
            name: 'quickstart',
            component: () => import('../pages/login/QuickStart.vue'),
          },
          {
            path: 'login/mfa',
            name: 'mfa',
            component: () => import('../pages/login/MfaVerify.vue'),
            beforeEnter: ifPendingMfa,
          },
          {
            path: 'login/mfa-setup',
            name: 'mfaSetup',
            component: () => import('../pages/login/MfaSetup.vue'),
            beforeEnter: ifPendingMfaSetup,
          },
          {
            path: 'login/reset',
            name: 'resetPassword',
            component: () => import('../pages/login/ResetPassword.vue'),
            beforeEnter: ifPendingReset,
          },
        ],
      },
      {
        path: '/blocked/',
        component: () => import('../layouts/noLayout.vue'),
        children: [
          {
            path: '',
            name: 'blocked',
            component: () => import('../pages/BlockedPage.vue'),
          },
        ],
      },
      // TODO: Remove in place of the new RFS V3 dashboard system
      {
        path: '/intake',
        component: () => import('../layouts/noLayout.vue'),
        beforeEnter: [ifAuthenticated, checkGeofence],
        children: [
          {
            path: 'rfs',
            name: 'intakeRfs',
            component: () => import('../pages/intake/rfs/RFSMoved.vue'),
            props: {
              lang: 'en-US',
            },
          },
          {
            path: 'dds',
            name: 'intakeDds',
            component: () => import('../pages/intake/rfs/RFSMoved.vue'),
            props: {
              lang: 'fr-CA',
            },
          },
          {
            path: 'confirm/evaluation/:evaluation/record/:patientRecordID',
            name: 'intakeRfsConfirmation',
            component: () => import('../pages/intake/rfs/RfsConfirmation.vue'),
            props: true,
          },
        ],
      },
      {
        path: '/referral',
        component: () => import('../layouts/NoAuthIntakeFormLayout.vue'),
        beforeEnter: [reloadIntoBearerMode, ifNotAuthenticated, checkGeofence],
        children: [
          {
            path: 'case/:patientRecordGUID/doctor/:employeeGUID',
            name: 'referral',
            component: () => import('../pages/Referral.vue'),
            props: true,
          },
          {
            path: 'notallowed',
            name: 'notallowed',
            component: () => import('../pages/Error404.vue'),
          },
        ],
      },
      {
        path: '/attachment-request',
        name: 'attachment-request',
        component: () => import('../pages/AttachmentRequestPage.vue'),
      },
      {
        path: '/',
        component: () => import('../layouts/NoAuthLayout.vue'),
        beforeEnter: [checkGeofence],
        children: [
          {
            path: 'login/change',
            name: 'change',
            component: () => import('../pages/login/ChangePassword.vue'),
            beforeEnter: ifBearerMode,
          },
        ],
      },
      {
        path: '/home',
        component: () => import('../layouts/MainLayout.vue'),
        beforeEnter: [ifAuthenticated, checkGeofence],
        children: [
          { path: '', name: 'home', component: () => import('../pages/Home.vue') },
          {
            path: 'call',
            name: 'call',
            component: () => import('../pages/PrCall.vue'),
          },
          { path: 'activate', name: 'activate', component: () => import('../pages/Activate.vue') },
          {
            path: 'settings',
            name: 'settings',
            component: () => import('../pages/PrSettings.vue'),
          },
          {
            path: 'doctoractivate',
            name: 'doctoractivate',
            component: () => import('../pages/DoctorActivate.vue'),
          },
          { path: 'admin', name: 'admin', component: () => import('../pages/Admin.vue') },
          {
            path: 'cases/:caseNumber',
            name: 'case',
            component: () => import('../pages/Cases.vue'),
          },
          { path: 'cases', name: 'cases', component: () => import('../pages/Cases.vue') },
          { path: 'support', name: 'support', component: () => import('../pages/Support.vue') },
          { path: 'location', name: 'location', component: () => import('../pages/Location.vue') },
          {
            path: 'locations',
            name: 'locations',
            component: () => import('../pages/Locations.vue'),
          },
          {
            path: 'medkit',
            component: () => import('../layouts/noLayout.vue'),
            children: [
              {
                path: '',
                name: 'medkit',
                component: () => import('../pages/medkit/MedKit.vue'),
              },
              {
                path: 'import',
                name: 'medkitimport',
                component: () => import('../pages/medkit/MedkitImport.vue'),
              },
              {
                path: 'new',
                name: 'medication-new',
                component: () => import('../pages/medkit/Medication.vue'),
                props: { isNew: true },
              },
              {
                path: 'custom-fields',
                name: 'medicationcustomfields',
                component: () => import('../pages/medkit/MedicationCustomFields.vue'),
              },
              {
                path: 'custom-fields/new',
                name: 'newmedicationcustomfield',
                component: () => import('../pages/medkit/MedicationCustomField.vue'),
              },
              {
                path: 'custom-fields/manage/:id',
                name: 'managemedicationcustomfield',
                component: () => import('../pages/medkit/MedicationCustomField.vue'),
                props: true,
              },
              {
                path: ':medicationID',
                name: 'medication-view',
                component: () => import('../pages/medkit/Medication.vue'),
                props: true,
                beforeEnter: validMedicationId,
              },
            ],
          },
          { path: 'members', name: 'members', component: () => import('../pages/Members.vue') },
          {
            path: 'opencases',
            name: 'opencases',
            component: () => import('../pages/HomeAdmin.vue'),
          },
          {
            path: 'reports',
            name: 'reports',
            component: () => import('../pages/reports/Reports.vue'),
          },
          {
            path: 'clientdashboard',
            name: 'clientdashboard',
            component: () => import('../pages/HomeClient.vue'),
          },
          { path: 'profile', name: 'profile', component: () => import('../pages/Profile.vue') },
          { path: 'schedule', name: 'schedule', component: () => import('../pages/Schedule.vue') },
          // Who's on call (PRX-2237). No route-level role check: the page gates itself,
          // so client-level doctors can reach it.
          { path: 'oncall', name: 'oncall', component: () => import('../pages/OnCall.vue') },
          {
            path: 'workflows',
            component: () => import('../layouts/noLayout.vue'),
            children: [
              {
                path: 'templates',
                name: 'workflowtemplates',
                component: () => import('../pages/workflows/WorkflowTemplates.vue'),
              },
              {
                path: 'template/new',
                name: 'newworkflowtemplate',
                component: () => import('../pages/workflows/manageWorkflowTemplate.vue'),
              },
              {
                path: 'template/edit/:workflowGUID',
                name: 'editworkflowtemplate',
                component: () => import('../pages/workflows/manageWorkflowTemplate.vue'),
              },
              {
                path: 'triggers',
                name: 'workflowtriggers',
                component: () => import('../pages/workflows/WorkflowTriggers.vue'),
              },
              {
                path: 'triggers/new',
                name: 'newworkflowtrigger',
                component: () => import('../pages/workflows/manageWorkflowTrigger.vue'),
              },
              {
                path: 'triggers/edit/:triggerGUID',
                name: 'editworkflowtrigger',
                component: () => import('../pages/workflows/manageWorkflowTrigger.vue'),
              },
            ],
          },
          {
            path: 'protocols',
            component: () => import('../layouts/noLayout.vue'),
            children: [
              {
                path: '',
                name: 'protocols',
                component: () => import('../pages/protocols/Protocols.vue'),
              },
              {
                path: 'new',
                name: 'newprotocol',
                component: () => import('../pages/protocols/PrManageProtocolMap.vue'),
              },
              {
                path: 'edit/:protocolMapGUID',
                name: 'editProtocolMap',
                component: () => import('../pages/protocols/PrManageProtocolMap.vue'),
              },
            ],
          },
          {
            path: 'reports',
            component: () => import('../layouts/noLayout.vue'),
            children: [
              {
                path: 'telemed',
                name: 'telemedreport',
                component: () => import('../pages/reports/TelemedReport.vue'),
              },
              {
                path: 'medicationslog',
                name: 'medicationslog',
                component: () => import('../pages/reports/MedicationsLog.vue'),
                meta: { requiresMedKit: true },
              },
              {
                path: 'custom',
                name: 'customreport',
                component: () => import('../pages/reports/CustomCaseReport.vue'),
              },
              {
                path: 'prescription',
                name: 'prescriptionreport',
                component: () => import('../pages/reports/PrescriptionReport.vue'),
              },

              {
                path: 'undispensedprescriptions',
                name: 'undispensedprescriptionsreport',
                component: () => import('../pages/reports/UndispensedPrescriptionsReport.vue'),
              },
              {
                path: 'medication',
                name: 'medicationreport',
                component: () => import('../pages/reports/MedicationReport.vue'),
                meta: { requiresMedKit: true },
              },
            ],
          },
        ],
      },
      {
        path: '/parks',
        component: () => import('../layouts/MainLayout.vue'),
        beforeEnter: [ifRfsAuthenticated, checkGeofence],
        children: [
          {
            path: 'dashboard',
            name: 'rfsDashboard',
            component: () => import('../pages/parks/RFSDashboard.vue'),
          },
          {
            path: 'rfs',
            name: 'rfs',
            component: () => import('../pages/intake/rfs/RfsIntake.vue'),
          },
          {
            path: 'dds',
            name: 'dds',
            component: () => import('../pages/intake/rfs/RfsIntake.vue'),
          },
          {
            path: 'confirm/evaluation/:evaluation/record/:patientRecordID',
            name: 'rfsConfirmation',
            component: () => import('../pages/intake/rfs/RfsConfirmation.vue'),
            props: true,
          },
          {
            path: 'rfs-support',
            name: 'rfsSupport',
            component: () => import('../pages/RfsSupport.vue'),
          },
          {
            path: 'settings',
            name: 'rfsSettings',
            component: () => import('../pages/PrSettings.vue'),
          },
        ],
      },
      {
        path: '/avatar/:memberId',
        name: 'avatar',
        component: () => import('../components/PrAvatar.vue'),
        beforeEnter: [ifAuthenticated, checkGeofence],
        props: true,
      },
      {
        path: '/about',
        name: 'about',
        beforeEnter: [checkGeofence],
        component: () => import('../pages/About.vue'),
      },
      // Always leave this as last one,
      // but you can also remove it
      {
        path: '/:catchAll(.*)*',
        beforeEnter: [checkGeofence],
        component: () => import('../pages/Error404.vue'),
      },
    ],

    history: createWebHistory(import.meta.env.BASE_URL),
  });

  Router.beforeEach(requireMedKit);

  return Router;
}
